import type { JSX, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { memo } from '@change-corgi/core/react/core';
import { parseHtml } from '@change-corgi/core/react/html';
import { Translate } from '@change-corgi/core/react/i18n';
import { FakeLink } from '@change-corgi/design-system-legacy/components/actions';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { isLongText } from 'src/app/pages/movement/shared/utilities';

export const CollapsableText = memo(function CollapsableText({
	text,
	anchorRef,
}: {
	text: string;
	// FIXME passing a ref is not ideal
	// it would be better to transform this into a onCollapsed callback
	// and have the "scollIntoView" be done where the element is rendered
	anchorRef: RefObject<HTMLDivElement>;
}): JSX.Element {
	const [hasLongText, setHasLongText] = useState<boolean | undefined>();
	const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
	const aboutTextRef = useRef<HTMLDivElement>(null);

	// Check if text is long enough to require a 'Read more' toggle
	useEffect(() => {
		if (text && hasLongText === undefined) {
			if (aboutTextRef.current && isLongText(aboutTextRef.current)) {
				// eslint-disable-next-line react-hooks/set-state-in-effect
				setHasLongText(true);
			} else {
				setHasLongText(false);
				setIsCollapsed(false);
			}
		}
	}, [text, hasLongText]);

	const expandText = useCallback(() => {
		setIsCollapsed(false);
	}, []);

	const collapseText = useCallback(() => {
		setIsCollapsed(true);
		anchorRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [anchorRef]);

	return (
		<Box>
			{isCollapsed ? (
				<Box ref={aboutTextRef} sx={{ overflowY: 'hidden', maxHeight: 240, position: 'relative' }}>
					<Box
						sx={{
							position: 'absolute',
							width: '100%',
							height: '100%',
							background: 'linear-gradient(to bottom, rgba(255,255,255,0) 80%, white 100%)',
						}}
					/>
					<Text>{parseHtml(text)}</Text>
				</Box>
			) : (
				<Text>{parseHtml(text)}</Text>
			)}
			{hasLongText && (
				<Text
					color="red.brand"
					sx={{
						cursor: 'pointer',
						textDecoration: 'underline',
						display: 'inline-block',
						width: '100%',
						textAlign: 'center',
					}}
					mx="auto"
					data-testid="movement-body-toggle"
				>
					{isCollapsed ? (
						<FakeLink onClick={expandText} data-testid="movement-body-expand" data-qa="movement-body-expand">
							<Translate value="corgi.movement.show.read_more" />
						</FakeLink>
					) : (
						<FakeLink onClick={collapseText} data-testid="movement-body-collapse" data-qa="movement-body-collapse">
							<Translate value="corgi.movement.show.read_less" />
						</FakeLink>
					)}
				</Text>
			)}
		</Box>
	);
});
